'use client'
import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowRight, Target, Clock, Play, Sparkles } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/components/user-portal/learner-i18n'
import { StarIcon } from './medallions/star-icon'
import { difficultyLabel } from './catalogue-text'

export interface NextRoundCTAProps {
  /** Defaults to "Your next training round" in the reader's language. */
  title?: string
  difficulty: string        // e.g. 'T3 · Medium'
  durationMin: number
  expectedStars: number
  pillarFocusCode: string   // e.g. 'PF3'
  /**
   * REQUIRED, with no default. It used to default to '/pt-play' — a route that
   * has never existed in this app (`git log -S pt-play -- apps/web/app` finds
   * no commit that added it); 080bd99e removed the same dead route from the
   * .eml builders but left the component defaults behind. The single caller
   * passes '/play/game', so the default was inert — and would have become a
   * live 404 the moment a second caller omitted the prop, which no guard could
   * see because the eml-links guard only inspects generated .eml strings.
   * Requiring it makes `tsc` the guard.
   */
  playHref: string
  /** When true, masks the tier/difficulty and focus pillar with a teaser. */
  mystery?: boolean
}

export function NextRoundCTA({
  title,
  difficulty,
  durationMin,
  expectedStars,
  pillarFocusCode,
  playHref,
  mystery,
}: NextRoundCTAProps) {
  const { t } = useTranslation()
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 p-5">
      <div className="absolute -top-12 -end-12 w-40 h-40 rounded-full opacity-30 blur-3xl bg-primary" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
          <Play className="h-7 w-7 fill-current" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">{t('learner.cards.nextRound.eyebrow')}</div>
          <div className="text-base font-semibold text-foreground">{title ?? t('learner.cards.nextRound.title')}</div>
          {mystery ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> {t('learner.cards.nextRound.mystery')}
              </span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t('learner.cards.nextRound.duration', { count: durationMin })}</span>
              <span>{t('learner.cards.nextRound.difficultyHidden')}</span>
            </div>
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {difficultyLabel(t, difficulty)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t('learner.cards.nextRound.duration', { count: durationMin })}</span>
              <span className="inline-flex items-center gap-1">
                {richText(t('learner.cards.nextRound.upToStars', { count: expectedStars }), {
                  star: <StarIcon size={11} />,
                  stars: <span className="font-semibold text-foreground">{expectedStars}</span>,
                })}
              </span>
              <span className="inline-flex items-center gap-1">
                {richText(t('learner.cards.nextRound.focus'), {
                  code: <Badge variant="outline" className="text-[10px]">{pillarFocusCode}</Badge>,
                })}
              </span>
            </div>
          )}
        </div>
        <Button asChild size="lg" className="shadow-lg">
          <Link href={playHref}>{t('learner.cards.nextRound.play')} <ArrowRight className="h-4 w-4 ms-1 rtl-flip" /></Link>
        </Button>
      </div>
    </div>
  )
}
